import { useState } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { compose } from 'recompose';
import withAuthProtection from 'hoc/withAuthProtection';
import withPermissionProtection from 'hoc/withPermissionProtection';

import Grid from '@material-ui/core/Grid';
import Typography from 'components/CncUI/Typography';
import SearchControls from './SearchControls';
import SuccessRecords from './SuccessRecords';
import LogsTable from './LogsTable';

import useQuery from 'hooks/useQuery';
import useSnackbar from 'hooks/useSnackbar';

import isEmail from 'utilities/validateEmail';
import format from 'utilities/format';

const useStyles = makeStyles(({ palette, breakpoints }) => ({
  header: {
    marginTop: 40,
    marginBottom: 0,
    [breakpoints.up('lg')]: {
      fontSize: 48,
      lineHeight: '56px',
    },
  },
  link: {
    color: palette.cncUI.misc.link,
  },
  tableContainer: {
    overflowX: 'auto',
  },
}));

const RegistrationLogsViewer = () => {
  const classes = useStyles();
  const notify = useSnackbar();
  const getRegistrationLogs = useQuery('GetEmployeeRegistrations', {
    scopes: 'employee_registrations:read',
    fetchPolicy: 'network-only',
    request: (ssn, email, idType) => ({
      email,
      lastFourSsn: ssn.length === 4 ? ssn : '',
      ssn: ssn.length > 4 ? format[idType](ssn) : '',
    }),
    response: res => res.data.getEmployeeRegistrations,
  });
  const getSuccessfulRegistrations = useQuery('getRegistrationStatus', {
    scopes: 'employee_registrations:read',
    fetchPolicy: 'network-only',
    request: (ssn, email, idType) => ({
      email,
      lastFourSsn: ssn.length === 4 ? ssn : '',
      ssn: ssn.length > 4 ? format[idType](ssn) : '',
    }),
    response: res => res.data.getRegistrationStatus,
  });
  const [state, setState] = useState({
    loading: false,
    searchValue: '',
    clearCache: false,
    logs: [],
    successLogs: [],
  });

  const modify = data => setState(old => ({ ...old, ...data }));

  const onClear = () =>
    modify({
      loading: false,
      searchValue: '',
      idType: 'ssn',
      clearCache: false,
      logs: [],
      successLogs: [],
    });

  const onUpdate = (value, idType) => modify({ searchValue: value, idType });

  const search = value => {
    modify({ loading: true, searchValue: value });
    const { idType } = state;
    const email = isEmail(value) ? value : '';
    const ssn = !isEmail(value) ? value : '';
    Promise.all([
      getRegistrationLogs(ssn, email, idType),
      getSuccessfulRegistrations(ssn, email, idType),
    ])
      .then(results => {
        modify({
          logs: results[0],
          successLogs: results[1],
          searchValue: value,
        });
      })
      .catch(err => {
        notify({
          type: 'error',
          show: true,
          message: err.message,
        });
      })
      .finally(() => modify({ loading: false }));
  };

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="h2" id="HeaderTitle" className={classes.header}>
          Registration Log Viewer
        </Typography>
      </Grid>
      <Grid item xs={12}>
        <SearchControls
          loading={state.loading}
          onSearch={search}
          onClear={onClear}
          onUpdate={onUpdate}
        />
      </Grid>
      <Grid item xs={12}>
        <LogsTable
          records={state.logs}
          loading={state.loading}
          searchValue={state.searchValue}
        />
      </Grid>
      <Grid item xs={12}>
        <SuccessRecords
          records={state.successLogs}
          loading={state.loading}
          searchValue={state.searchValue}
        />
      </Grid>
    </Grid>
  );
};

export default compose(
  withAuthProtection(),
  withPermissionProtection([
    'employee_registrations:read',
    'employee_registrations:edit',
  ]),
)(RegistrationLogsViewer);
